// src/pages/public/PrivacyPolicy.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, Shield, Lock, Eye, Database, UserCheck, ArrowRight, Mail } from 'lucide-react'

const PrivacyPolicy = () => {
  const [openSections, setOpenSections] = useState({})

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: Shield,
      content: `TGFuture ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.`
    },
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: Database,
      content: `We collect personal information that you provide directly to us, including name, email address, phone number, identification documents, and financial information necessary for investment activities.`
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      icon: UserCheck,
      content: `We use your information to provide and improve our services, process transactions, verify your identity, communicate with you, and comply with legal obligations.`
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing',
      icon: Eye,
      content: `We do not sell your personal information. We may share information with trusted third parties who assist us in operating our platform, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.`
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      content: `We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.`
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      content: `We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.`
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      content: `You have the right to access, correct, or delete your personal information. You may also have the right to restrict or object to our processing of your personal information, and the right to data portability.`
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      content: `We use cookies and similar tracking technologies to track activity on our platform and hold certain information to enhance your user experience and analyze platform usage.`
    },
    {
      id: 'international-transfer',
      title: 'International Data Transfers',
      content: `Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ.`
    },
    {
      id: 'changes',
      title: 'Changes to Privacy Policy',
      content: `We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.`
    }
  ]

  const dataProtectionPrinciples = [
    "Lawfulness, fairness, and transparency",
    "Purpose limitation",
    "Data minimization",
    "Accuracy",
    "Storage limitation",
    "Integrity and confidentiality",
    "Accountability"
  ]

  const features = [
    {
      icon: Shield,
      title: "GDPR Compliant",
      description: "Full compliance with international data protection standards",
      color: "from-[#9E1C60] to-[#811844]"
    },
    {
      icon: Lock,
      title: "Bank-Level Security",
      description: "256-bit encryption and advanced security measures",
      color: "from-[#F5AD18] to-[#9E1C60]"
    },
    {
      icon: Database,
      title: "Data Minimization",
      description: "We only collect essential information",
      color: "from-[#811844] to-[#561530]"
    },
    {
      icon: UserCheck,
      title: "Your Control",
      description: "Full control over your personal data",
      color: "from-[#561530] to-[#9E1C60]"
    }
  ]

  const dataRights = [
    { right: "Access", description: "Right to access your personal data" },
    { right: "Rectification", description: "Right to correct inaccurate data" },
    { right: "Erasure", description: "Right to be forgotten" },
    { right: "Restriction", description: "Right to restrict processing" },
    { right: "Portability", description: "Right to data portability" },
    { right: "Objection", description: "Right to object to processing" }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#561530] via-[#811844] to-[#9E1C60] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Shield className="w-16 h-16 mx-auto mb-6 text-[#F5AD18]" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Privacy{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5AD18] to-[#9E1C60]">
                Policy
              </span>
            </h1>
            <p className="text-xl text-[#F5AD18] mb-8 leading-relaxed">
              Your privacy is important to us. Learn how we protect and handle your personal information.
            </p>
            <div className="text-lg text-white/80">
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Banner */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className={`bg-gradient-to-r ${feature.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GDPR Compliance Notice */}
      <section className="py-12 bg-[#561530]/10 dark:bg-[#9E1C60]/20 border-b border-[#561530]/20 dark:border-[#9E1C60]/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h3 className="text-lg font-bold text-[#561530] dark:text-[#F5AD18] mb-4">
              GDPR Compliant & International Standards
            </h3>
            <p className="text-[#561530] dark:text-[#F5AD18]/80">
              TGFuture is fully compliant with the General Data Protection Regulation (GDPR) 
              and other international data protection standards to ensure your information is always protected.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Data Protection Principles */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-3">
                <div className="bg-gradient-to-r from-[#9E1C60] to-[#811844] w-8 h-8 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 text-white" />
                </div>
                <span>Data Protection Principles</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {dataProtectionPrinciples.map((principle, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="w-6 h-6 bg-[#F5AD18] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-[#561530] rounded-full"></div>
                    </div>
                    <span className="text-gray-700 dark:text-gray-300 text-sm">
                      {principle}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Policy Sections */}
            <div className="space-y-4">
              {sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-[#9E1C60] dark:hover:border-[#F5AD18] transition-colors"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      {section.icon && (
                        <div className="bg-gradient-to-r from-[#561530] to-[#9E1C60] w-10 h-10 rounded-xl flex items-center justify-center">
                          <section.icon className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {section.title}
                      </h3>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        openSections[section.id] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {openSections[section.id] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
                        {section.content}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Your Rights Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-gradient-to-r from-[#561530] to-[#811844] rounded-2xl p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6 text-center">
                Your Data Protection Rights
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dataRights.map((item, index) => (
                  <div key={index} className="text-center p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                    <div className="text-lg font-bold mb-2 text-[#F5AD18]">{item.right}</div>
                    <div className="text-white/80 text-sm">{item.description}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Contact Our Data Protection Officer
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg">
                If you have any questions about this Privacy Policy or your data protection rights, 
                please contact our Data Protection Officer.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-[#F5AD18] text-[#561530] hover:bg-[#F5AD18]/90 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center gap-2 shadow-lg"
                >
                  <Mail className="w-5 h-5" />
                  Contact Support
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="mailto:support@tgfuture.com"
                  className="border-2 border-[#561530] text-[#561530] dark:border-[#F5AD18] dark:text-[#F5AD18] hover:bg-[#561530] hover:text-white dark:hover:bg-[#F5AD18] dark:hover:text-[#561530] px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2"
                >
                  <Shield className="w-5 h-5" />
                  Email Support
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default PrivacyPolicy